import { Label as PomodoroLabel, Text as PomodoroText } from '@ifood/pomodoro-components';
import tokens from '@ifood/pomodoro-tokens';
import styled from 'styled-components';
import {
  BottomProps,
  FlexboxProps,
  LayoutProps,
  LeftProps,
  RightProps,
  SpaceProps,
  TopProps,
  bottom,
  flexbox,
  layout,
  left,
  right,
  space,
  top,
} from 'styled-system';

type AbsoluteProps = RightProps & BottomProps & TopProps & LeftProps;

export const Banner = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  padding-bottom: 92px;
  position: relative;
  overflow: hidden;

  @media (max-width: ${tokens.breakpoints.s}) {
    padding-bottom: 20px;
  }
`;

export const Column = styled.div`
  width: 50%;

  @media (max-width: ${tokens.breakpoints.m}) {
    width: 100%;
  }
`;

export const Text = styled(PomodoroText)`
  display: block;
  margin-bottom: ${tokens.space.largest};
  margin-top: ${tokens.space.large};
`;

export const ObjectContainer = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  z-index: -1;
`;

export const ObjectImage = styled.img<AbsoluteProps>`
  position: absolute;
  user-drag: none;
  user-select: none;

  ${right};
  ${left};
  ${bottom};
  ${top};

  @media (max-width: ${tokens.breakpoints.s}) {
    display: none;
  }
`;

export const Container = styled.div<FlexboxProps>`
  width: 100%;
  padding: 40px 0;
  display: flex;
  flex-wrap: wrap;
  max-width: 1130px;

  ${flexbox};

  @media (max-width: ${tokens.breakpoints.s}) {
    padding: 20px 0;
  }
`;

type BackgroundProps = {
  src?: string;
} & LayoutProps &
  SpaceProps;

export const Background = styled.div<BackgroundProps>`
  background: ${({ src }): string | undefined => src && `url(${src})`};
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
  display: flex;
  position: relative;

  ${space};
  ${layout};

  @media (max-width: ${tokens.breakpoints.s}) {
    display: none;
  }
`;

export const VideoSrc = styled.video<LayoutProps & AbsoluteProps>`
  position: absolute;
  object-fit: fill;

  ${right};
  ${left};
  ${bottom};
  ${top};

  ${layout};

  &:hover {
    cursor: zoom-in;
  }
`;

type SectionProps = {
  background?: string;
} & FlexboxProps;

export const Section = styled.section<SectionProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  ${({ background }): string | undefined => background && `background-color: ${background}`};

  ${flexbox};
`;

export const CheckList = styled.ul`
  list-style: none;
  padding: 0;
`;

export const CheckItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: ${tokens.space.regular};

  &:first-child {
    margin-top: ${tokens.space.larger};
  }
`;

export const Label = styled(PomodoroLabel)`
  font-weight: bold;
  font-size: ${tokens.fontSizes.s};
  line-height: ${tokens.lineHeights.m};
  color: ${tokens.colors.black};
  margin-left: ${tokens.space.regular};
`;

export const Blocks = styled.div`
  display: grid;
  grid-gap: ${tokens.space.loosest};
  grid-template-columns: repeat(auto-fill, 260px);
  justify-content: center;
  width: 100%;
  margin-top: ${tokens.space.looser};
  @media (max-width: ${tokens.breakpoints.m}) {
    grid-gap: ${tokens.space.regular};
  }
`;

export const Block = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  border: 1px solid ${tokens.colors.primary};
  border-radius: ${tokens.radii.s};
  padding: ${tokens.space.large};
`;

export const SmallText = styled(PomodoroText)`
  font-size: 14px;
  line-height: 18px;
  display: block;
  &:not(:last-child) {
    margin-bottom: ${tokens.space.regular};
  }
`;

export const BlockIcon = styled.img`
  width: 32px;
  margin: 0 auto;
  margin-bottom: ${tokens.space.large};
`;

export const ServiceBlock = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  border: 1px solid ${tokens.colors.primary};
  border-radius: 24px;
  padding: ${tokens.space.larger};
  background: ${tokens.colors.white};
`;

type ServiceImageProps = {
  right?: boolean;
};

export const ServiceImage = styled.img<ServiceImageProps>`
  margin-right: ${({ right }) => !right && tokens.space.larger};
  margin-left: ${({ right }) => right && tokens.space.larger};
  @media (max-width: ${tokens.breakpoints.s}) {
    margin-right: 0;
    margin-bottom: ${tokens.space.larger};
  }
`;

export const SubTitle = styled(PomodoroText)`
  font-size: ${tokens.fontSizes.l};
  font-weight: 500;
  line-height: ${tokens.lineHeights.l};
`;
